import * as Menu from '@radix-ui/react-dropdown-menu'
import { clsx } from 'clsx'
import { orderBy } from 'firebase/firestore'
import { useMemo, useState, type ReactNode } from 'react'
import { ADMIN_ROLE_ID, MEMBER_ROLES, TEST_ACCOUNTS, type ClientDoc, type RoleDoc, type StaffAccessDoc, type TestAccount } from '@sonder/shared'
import { useAuth } from '@/auth/AuthProvider'
import { itemClass } from '@/auth/menuStyles'
import { signInAs, switchError, type SignInTarget } from '@/auth/impersonation'
import { useMembers } from '@/data/client'
import { useCollection } from '@/data/hooks'
import { Avatar, useToast } from '@/ui'

type UserDoc = { email?: string; displayName?: string; role?: 'staff' | 'client'; clientIds?: string[]; photoURL?: string | null; test?: boolean }
type Person = { key: string; target: SignInTarget; name: string; detail: string; photo?: string | null; client: boolean }

/** Show a search box once the list is long enough to need one. */
const SEARCH_FROM = 8

/**
 * The "Sign in as" side menu (admins only): the built-in test accounts, the Sonder team, then each client's people.
 * Only people who have signed in at least once are listed, so an admin never sees someone's portal before they do.
 */
export function SignInAsMenu() {
  const { user } = useAuth()
  const { toast } = useToast()
  const users = useCollection<UserDoc>('users')
  const access = useCollection<StaffAccessDoc>('staffAccess')
  const roles = useCollection<RoleDoc>('roles')
  const clients = useCollection<ClientDoc>('clients', [orderBy('name')])
  const [firstVisit, setFirstVisit] = useState(false)
  const [query, setQuery] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const q = query.trim().toLowerCase()

  const byUid = useMemo(() => new Map(users.items.map((u) => [u.id, u])), [users.items])
  // The team is whoever still has console access; a removed teammate's user record stays behind and is left out.
  const team = useMemo<Person[]>(() => {
    const byEmail = new Map(access.items.filter((a) => !a.test).map((a) => [a.email.toLowerCase(), a]))
    const roleName = (id: string) => (id === ADMIN_ROLE_ID ? 'Admin' : (roles.items.find((r) => r.id === id)?.name ?? id))
    return users.items
      .filter((u) => u.id !== user?.uid && !u.test && u.email && byEmail.has(u.email.toLowerCase()))
      .map((u) => {
        const a = byEmail.get(u.email!.toLowerCase())!
        return { key: u.id, target: { targetUid: u.id }, name: u.displayName || a.name || u.email!, detail: roleName(a.roleId), photo: u.photoURL, client: false }
      })
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [users.items, access.items, roles.items, user?.uid])
  const tests: Person[] = (Object.keys(TEST_ACCOUNTS) as TestAccount[]).map((k) => ({
    key: `test:${k}`,
    target: { test: k },
    name: TEST_ACCOUNTS[k].name,
    detail: TEST_ACCOUNTS[k].label,
    client: k === 'client',
  }))
  const realClients = clients.items.filter((c) => !c.test)

  const go = async (p: Person) => {
    setBusy(p.key)
    try {
      await signInAs(p.target, p.client && firstVisit)
    } catch (e) {
      setBusy(null)
      toast(switchError(e), 'error')
    }
  }
  const row = (p: Person) => <PersonItem key={p.key} person={p} busy={busy} onPick={go} />

  const loading = users.loading || access.loading || clients.loading
  const showSearch = !loading && team.length + users.items.filter((u) => u.role === 'client').length >= SEARCH_FROM
  // The switch and test accounts need no data, so they show at once: the menu opens at full size and stays open
  // while the pointer travels to it, instead of starting as a small "Loading" box the pointer can slip off.
  return (
    <>
      <Menu.CheckboxItem
        checked={firstVisit}
        onCheckedChange={setFirstVisit}
        // Keep the menu open: this sets how the next pick opens, it isn't the pick.
        onSelect={(e) => e.preventDefault()}
        className={clsx(itemClass, 'items-start')}
      >
        <span aria-hidden="true" className={clsx('relative mt-0.5 h-4 w-7 shrink-0 border transition-colors', firstVisit ? 'border-ink bg-ink' : 'border-gray-300 bg-gray-300')}>
          <span className={clsx('absolute top-px h-3 w-3 bg-paper transition-transform', firstVisit ? 'translate-x-3.5' : 'translate-x-px')} />
        </span>
        <span>
          <span className="block text-ink">First visit</span>
          <span className="block text-[0.75rem] leading-snug text-dim">Client portals open on the welcome introduction. Their own record isn’t changed.</span>
        </span>
      </Menu.CheckboxItem>
      <Menu.Separator className="my-2 h-px bg-gray-200" />
      <Group label="Test accounts">{tests.map(row)}</Group>
      {showSearch && (
        <div className="px-4 pb-2">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            // Typing belongs to the box, not the menu's type-to-jump; arrows and Escape still move through the menu.
            onKeyDown={(e) => {
              if (!['ArrowDown', 'ArrowUp', 'Escape', 'Tab'].includes(e.key)) e.stopPropagation()
            }}
            placeholder="Search people"
            aria-label="Search people"
            className="w-full border-b border-rule bg-transparent py-1.5 text-small outline-none placeholder:text-dim focus:border-ink"
          />
        </div>
      )}
      <div className="max-h-[min(55vh,26rem)] overflow-y-auto">
        {loading ? (
          <p className="px-4 py-2.5 text-small text-dim">Loading people…</p>
        ) : (
          <>
            <Group label="Sonder team">{team.filter((p) => matches(p, q)).map(row)}</Group>
            {realClients.map((c) => (
              <ClientGroup key={c.id} client={c} query={q} selfUid={user?.uid ?? ''} byUid={byUid} row={row} />
            ))}
          </>
        )}
      </div>
    </>
  )
}

function matches(p: Person, q: string, extra = ''): boolean {
  return !q || `${p.name} ${p.detail} ${extra}`.toLowerCase().includes(q)
}

function ClientGroup(props: { client: ClientDoc & { id: string }; query: string; selfUid: string; byUid: Map<string, UserDoc>; row: (p: Person) => ReactNode }) {
  const members = useMembers(props.client.id)
  const people = members.items
    // A member exists once they accept their invite (their first sign-in); the account check guards a deleted one.
    .filter((m) => m.id !== props.selfUid && props.byUid.has(m.id))
    .map<Person>((m) => ({
      key: m.id,
      target: { targetUid: m.id },
      name: m.name || m.email,
      detail: [MEMBER_ROLES[m.role]?.label, m.title].filter(Boolean).join(' · '),
      photo: props.byUid.get(m.id)?.photoURL,
      client: true,
    }))
    .filter((p) => matches(p, props.query, props.client.name))
    .sort((a, b) => a.name.localeCompare(b.name))
  return <Group label={props.client.name}>{people.map(props.row)}</Group>
}

function Group({ label, children }: { label: string; children: ReactNode[] }) {
  if (!children.length) return null
  return (
    <Menu.Group className="pb-2">
      <Menu.Label className="px-4 pb-1 pt-2 text-[0.6875rem] uppercase tracking-eyebrow text-dim">{label}</Menu.Label>
      {children}
    </Menu.Group>
  )
}

function PersonItem({ person, busy, onPick }: { person: Person; busy: string | null; onPick: (p: Person) => void }) {
  return (
    <Menu.Item
      className={itemClass}
      disabled={Boolean(busy)}
      onSelect={(e) => {
        e.preventDefault()
        onPick(person)
      }}
    >
      <Avatar name={person.name} src={person.photo} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-ink">{busy === person.key ? 'Signing in…' : person.name}</span>
        <span className="block truncate text-[0.75rem] text-dim">{person.detail}</span>
      </span>
    </Menu.Item>
  )
}
